// FTEDN Creator — Referral Panel (share link, tribe stats)
import { useEffect, useState } from 'react';
import { apiClient } from '../../hooks/useApi';
import { generateReferralLink, calculateTribeStats, type TribeStats } from './community.helpers';
import { useToast } from '../../components/ui/Toast';
import './CommunityPage.css';

interface ReferralPanelProps {
  userId: string;
}

export function ReferralPanel({ userId }: ReferralPanelProps) {
  const [stats, setStats] = useState<TribeStats>({ totalInvites: 0, pendingInvites: 0, activeMembers: 0, xpEarned: 0 });
  const [loading, setLoading] = useState(false);
  const { addToast } = useToast();

  const referralLink = generateReferralLink(userId);

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    apiClient.get('/referral/tribe')
      .then(res => {
        if (res.ok && Array.isArray(res.referrals)) {
          setStats(calculateTribeStats(res.referrals));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [userId]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      addToast({ type: 'success', title: 'Copied!', message: 'Share this link with friends.' });
    } catch {
      addToast({ type: 'info', title: 'Link', message: referralLink });
    }
  };

  return (
    <div className="referral-panel">
      <div className="referral-header">
        <h2>🌱 Your Tribe</h2>
        <p className="referral-desc">Invite friends and grow your Eedun tribe. Earn XP for each active member!</p>
      </div>

      {/* Share link */}
      <div className="referral-link-box">
        <input className="neo-input referral-link-input" value={referralLink} readOnly />
        <button className="neo-btn neo-btn--primary" onClick={handleCopy}>📋 Copy</button>
      </div>

      {/* Tribe stats */}
      <div className="referral-stats">
        <div className="referral-stat">
          <span className="referral-stat-value">{loading ? '—' : stats.totalInvites}</span>
          <span className="referral-stat-label">Total Invites</span>
        </div>
        <div className="referral-stat">
          <span className="referral-stat-value">{loading ? '—' : stats.activeMembers}</span>
          <span className="referral-stat-label">Active</span>
        </div>
        <div className="referral-stat">
          <span className="referral-stat-value">{loading ? '—' : stats.pendingInvites}</span>
          <span className="referral-stat-label">Pending</span>
        </div>
        <div className="referral-stat">
          <span className="referral-stat-value">{loading ? '—' : `+${stats.xpEarned}`}</span>
          <span className="referral-stat-label">XP Earned</span>
        </div>
      </div>
    </div>
  );
}
